// Port de Leyro Footer.jsx adaptado a chain.
//
// Cambios vs Leyro:
//   • Columnas reformuladas para o negocio de chain (animais), non
//     maquinaria/terras.
//   • Selector de idioma con 4 flags inline (gl + es activas; pt + en
//     deshabilitadas con `available: false`, igual que Leyro Header.jsx).
//     Sen prefixos na URL — chain non ten i18next routing aínda.

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { setLanguage, SUPPORTED_LANGS, type SupportedLang } from '../../i18n';

interface FooterColumn {
  headingKey: string;
  links: { labelKey: string; href: string; external?: boolean }[];
}

const COLUMNS: FooterColumn[] = [
  {
    headingKey: 'footer.forFarmers',
    links: [
      { labelKey: 'footer.sellAnimal', href: '/marketplace/vender' },
      { labelKey: 'footer.traceability', href: 'https://chain.leyro.es', external: true },
      { labelKey: 'footer.blockchain', href: 'https://chain.leyro.es#trust', external: true },
    ],
  },
  {
    headingKey: 'footer.forBuyers',
    links: [
      { labelKey: 'footer.catalog', href: '/marketplace' },
      { labelKey: 'footer.purchases', href: '/marketplace/me/purchases' },
      { labelKey: 'footer.auctions', href: '/marketplace?destination=AUCTION' },
    ],
  },
  {
    headingKey: 'footer.leyroSection',
    links: [
      { labelKey: 'footer.plans', href: '/marketplace/plans' },
      { labelKey: 'footer.terms', href: '/marketplace/termos' },
      { labelKey: 'footer.privacy', href: '/marketplace/privacidade' },
      { labelKey: 'footer.compliance', href: '/marketplace/cumprimento' },
    ],
  },
];

// Bandeiras SVG inline — non depende de emojis (renderizan distinto entre
// OS) nin de iconos externos. Mesmo set que Leyro `Header.jsx`.
function FlagGalicia({ size = 16 }: { size?: number }): JSX.Element {
  return (
    <svg
      viewBox="0 0 30 20"
      width={size}
      height={(size * 2) / 3}
      className="rounded-sm border border-border/40 shadow-sm"
      aria-hidden="true"
    >
      <rect width="30" height="20" fill="white" />
      <path d="M0 20 L30 0" stroke="#4A90E2" strokeWidth="4" />
    </svg>
  );
}
function FlagSpain({ size = 16 }: { size?: number }): JSX.Element {
  return (
    <svg
      viewBox="0 0 30 20"
      width={size}
      height={(size * 2) / 3}
      className="rounded-sm border border-border/40 shadow-sm"
      aria-hidden="true"
    >
      <rect width="30" height="5" fill="#AA151B" />
      <rect y="5" width="30" height="10" fill="#F1BF00" />
      <rect y="15" width="30" height="5" fill="#AA151B" />
    </svg>
  );
}
function FlagPortugal({ size = 16 }: { size?: number }): JSX.Element {
  return (
    <svg
      viewBox="0 0 30 20"
      width={size}
      height={(size * 2) / 3}
      className="rounded-sm border border-border/40 shadow-sm"
      aria-hidden="true"
    >
      <rect width="12" height="20" fill="#006600" />
      <rect x="12" width="18" height="20" fill="#FF0000" />
    </svg>
  );
}
function FlagUK({ size = 16 }: { size?: number }): JSX.Element {
  return (
    <svg
      viewBox="0 0 30 20"
      width={size}
      height={(size * 2) / 3}
      className="rounded-sm border border-border/40 shadow-sm"
      aria-hidden="true"
    >
      <rect width="30" height="20" fill="#012169" />
      <path d="M0 0 L30 20 M30 0 L0 20" stroke="white" strokeWidth="3" />
      <path d="M15 0 L15 20 M0 10 L30 10" stroke="white" strokeWidth="5" />
      <path d="M15 0 L15 20 M0 10 L30 10" stroke="#C8102E" strokeWidth="3" />
    </svg>
  );
}

interface LanguageEntry {
  code: 'gl' | 'es' | 'pt' | 'en';
  labelKey: string;
  flag: (props: { size?: number }) => JSX.Element;
  available: boolean;
}

const LANGUAGES: LanguageEntry[] = [
  { code: 'gl', labelKey: 'language.gl', flag: FlagGalicia, available: true },
  { code: 'es', labelKey: 'language.es', flag: FlagSpain, available: true },
  { code: 'pt', labelKey: 'language.pt', flag: FlagPortugal, available: false },
  { code: 'en', labelKey: 'language.en', flag: FlagUK, available: false },
];

function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const current = (i18n.language?.split('-')[0] ?? 'gl') as SupportedLang;
  return (
    <div className="flex items-center gap-2" role="group" aria-label={t('language.label')}>
      {LANGUAGES.map((lang) => {
        const Flag = lang.flag;
        const isActive = lang.available && current === lang.code;
        const disabled = !lang.available;
        return (
          <button
            key={lang.code}
            type="button"
            disabled={disabled}
            onClick={() => {
              if (disabled || isActive) return;
              // Só persistimos linguas soportadas polo i18n bundle (gl/es).
              // pt/en quedan reservadas — o bandeirín é visual e desactivado.
              if (SUPPORTED_LANGS.includes(lang.code as SupportedLang)) {
                void setLanguage(lang.code as SupportedLang);
              }
            }}
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs border transition-colors ${
              isActive
                ? 'border-dark bg-subtle font-semibold text-dark'
                : disabled
                  ? 'border-border text-muted opacity-50 cursor-not-allowed'
                  : 'border-border text-dark hover:bg-subtle cursor-pointer'
            }`}
            aria-pressed={isActive}
            aria-label={t(lang.labelKey)}
            title={disabled ? t('language.unavailable') : t(lang.labelKey)}
          >
            <Flag size={14} />
            <span className="uppercase">{lang.code}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-border bg-white">
      <div className="max-w-[1760px] mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div>
            <h3 className="text-sm font-semibold text-dark mb-4">{t('footer.helpHeading')}</h3>
            <ul className="space-y-3 mb-6">
              <li>
                <Link to="/marketplace/axuda" className="text-sm text-dark hover:underline">
                  {t('footer.helpCenter')}
                </Link>
              </li>
              <li>
                <Link to="/marketplace/seguridade" className="text-sm text-dark hover:underline">
                  {t('footer.safety')}
                </Link>
              </li>
            </ul>

            <h3 className="text-sm font-semibold text-dark mb-4">{t('footer.contactHeading')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/marketplace/contacto" className="text-sm text-dark hover:underline">
                  {t('footer.contactForm')}
                </Link>
              </li>
            </ul>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.headingKey}>
              <h3 className="text-sm font-semibold text-dark mb-4">{t(col.headingKey)}</h3>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.labelKey}>
                    {link.external ? (
                      <a
                        href={link.href}
                        className="text-sm text-dark hover:underline"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t(link.labelKey)}
                      </a>
                    ) : (
                      <Link to={link.href} className="text-sm text-dark hover:underline">
                        {t(link.labelKey)}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-border" />

      <div className="max-w-[1760px] mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-dark">
          <span>{t('footer.copyright')}</span>
          <span className="text-muted hidden sm:inline">·</span>
          <Link to="/marketplace/privacidade" className="hover:underline">
            {t('footer.privacyLink')}
          </Link>
          <span className="text-muted">·</span>
          <Link to="/marketplace/termos" className="hover:underline">
            {t('footer.termsLink')}
          </Link>
          <span className="text-muted">·</span>
          <Link to="/marketplace/sitemap" className="hover:underline">
            {t('footer.sitemap')}
          </Link>
        </div>

        <LanguageSwitcher />
      </div>
    </footer>
  );
}
